import React, { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Input, Select, Space, Table, Tag, notification } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import api, { IQueryParams } from '../../api'
import { PortalArtigoModel, StatusPortalArtigo } from '../../models/PortalArtigoModel'

const PAGE_SIZE = 10

export function PortalArtigosView() {
  const history = useHistory()
  const [artigos, setArtigos] = useState<PortalArtigoModel[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE })
  const [filters, setFilters] = useState<{ status?: StatusPortalArtigo; busca?: string }>({})

  const fetchArtigos = useCallback(
    async (nextFilters = filters, nextPagination = pagination) => {
      setLoading(true)
      try {
        const params: IQueryParams & { status?: string; busca?: string; page?: number } = {
          limit: nextPagination.limit,
          offset: (nextPagination.page - 1) * nextPagination.limit,
          order: 'DESC',
          orderBy: 'createdAt',
          ...nextFilters,
        }
        const response = await api.PortalArtigos.get(params)
        setArtigos(response.rows)
        setTotal(response.count)
      } catch (error) {
        notification.error({ message: error.message || 'Não foi possível carregar os artigos' })
      } finally {
        setLoading(false)
      }
    },
    [filters, pagination]
  )

  useEffect(() => {
    fetchArtigos()
  }, [])

  const handleFilterChange = (nextFilters: typeof filters) => {
    const mergedFilters = { ...filters, ...nextFilters }
    const firstPage = { ...pagination, page: 1 }
    setFilters(mergedFilters)
    setPagination(firstPage)
    fetchArtigos(mergedFilters, firstPage)
  }

  const handleTableChange = (tablePagination: any) => {
    const nextPagination = {
      page: tablePagination.current ?? pagination.page,
      limit: tablePagination.pageSize ?? pagination.limit,
    }
    setPagination(nextPagination)
    fetchArtigos(filters, nextPagination)
  }

  const columns = [
    {
      title: 'Título',
      dataIndex: 'titulo',
      key: 'titulo',
      render: (titulo: string, artigo: PortalArtigoModel) => (
        <Link to={`/portal-artigos/${artigo.id}`}>{titulo}</Link>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      width: 260,
      render: (slug: string) => (
        <a href={`https://newdriver.com.br/${slug}`} target="_blank" rel="noopener noreferrer">
          /{slug}
        </a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: StatusPortalArtigo) => (
        <Tag color={status === 'PUBLICADO' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 120,
      render: (tipo: PortalArtigoModel['tipo']) => (
        tipo === 'PILLAR' ? <Tag color="blue">Pillar</Tag> : <Tag>Comum</Tag>
      ),
    },
    {
      title: 'Publicado em',
      dataIndex: 'dataPublicacao',
      key: 'dataPublicacao',
      width: 140,
      render: (value: string) => (value ? new Date(value).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Atualizado em',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (value: string) => (value ? new Date(value).toLocaleDateString('pt-BR') : '-'),
    },
  ]

  return (
    <Layout>
      <PageHeader>
        <Title>Artigos do Portal</Title>
        <Button type="primary" onClick={() => history.push('/portal-artigos/novo')}>
          Novo artigo
        </Button>
      </PageHeader>

      <Filters>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Buscar"
            style={{ width: 260 }}
            onSearch={(busca) => handleFilterChange({ busca })}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={filters.status}
            onChange={(status) => handleFilterChange({ status })}
            options={[
              { value: 'RASCUNHO', label: 'Rascunho' },
              { value: 'PUBLICADO', label: 'Publicado' },
            ]}
          />
        </Space>
      </Filters>

      <Table
        columns={columns}
        dataSource={artigos}
        loading={loading}
        rowKey="id"
        scroll={{ x: 980 }}
        locale={{ emptyText: 'Não há artigos cadastrados.' }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (count, range) => `${range[0]}-${range[1]} de ${count} itens`,
        }}
        onChange={handleTableChange}
      />
    </Layout>
  )
}

const Layout = styled.div`
  padding: 24px;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
`

const Filters = styled.div`
  margin-bottom: 16px;
`
